import type { IconName, MessageKey } from '@signbento/ui'

/**
 * Navigation destinations — ADD-055: five on web; on a phone, three primary tabs and More (Insights
 * and Settings), so the bar holds four.
 */

export interface NavDestination {
  readonly id: string
  readonly path: string
  readonly label_key: MessageKey
  /** The glyph beside the label; the label always stays in the accessible name. */
  readonly icon: IconName
  readonly mobile_primary: boolean
}

export const NAV_DESTINATIONS: readonly NavDestination[] = [
  { id: 'home', path: '/', label_key: 'nav.home', icon: 'home', mobile_primary: true },
  {
    id: 'agreements',
    path: '/agreements',
    label_key: 'nav.agreements',
    icon: 'document',
    mobile_primary: true,
  },
  {
    id: 'templates',
    path: '/templates',
    label_key: 'nav.templates',
    icon: 'template',
    mobile_primary: true,
  },
  {
    id: 'insights',
    path: '/insights',
    label_key: 'nav.insights',
    icon: 'chart',
    mobile_primary: false,
  },
  {
    id: 'settings',
    path: '/settings',
    label_key: 'nav.settings',
    icon: 'settings',
    mobile_primary: false,
  },
]

export function mobilePrimaryDestinations(): readonly NavDestination[] {
  return NAV_DESTINATIONS.filter((destination) => destination.mobile_primary)
}

export function moreDestinations(): readonly NavDestination[] {
  return NAV_DESTINATIONS.filter((destination) => !destination.mobile_primary)
}

/**
 * Settings pages a phone reaches straight from More (PRD §3.1/line 443 puts My Info, signatures,
 * purchases and credits, and backup under More; mockup 1-02 #5 lists them there). Each opens its
 * Settings section on its own page, one tap instead of three.
 */
export interface MoreShortcut {
  readonly id: string
  readonly path: string
  readonly label_key: MessageKey
  readonly icon: IconName
}

export const MORE_SHORTCUTS: readonly MoreShortcut[] = [
  {
    id: 'my-info',
    path: '/settings?section=general',
    label_key: 'nav.more_my_info',
    icon: 'id-card',
  },
  {
    id: 'plan',
    path: '/settings?section=purchases',
    label_key: 'nav.more_plan',
    icon: 'briefcase',
  },
  { id: 'backup', path: '/settings?section=backup', label_key: 'nav.more_backup', icon: 'shield' },
  { id: 'help', path: '/settings?section=help', label_key: 'nav.more_help', icon: 'info' },
]

/** Routes below Settings: account, purchase, operator and MCP surfaces. */
const SETTINGS_ROUTES = ['/sign-in', '/account-actions/', '/purchases/', '/ops/', '/mcp/']

/**
 * The navigation entry a route belongs to (UX-34), so every screen highlights one: Sign is Home's
 * action, Send for signature is one flow with the editor and the request it makes (all
 * Agreements), and account surfaces sit under Settings. `parent` is where a phone's back control goes from a screen below the top level when
 * there is no earlier page in the app to go back to. A path the app doesn't know highlights
 * nothing (`id` null) and goes back to Home.
 */
export function navSectionFor(pathname: string): {
  readonly id: NavDestination['id'] | null
  readonly parent: string | null
} {
  const path = pathname.replace(/\/+$/, '') || '/'
  for (const destination of NAV_DESTINATIONS) {
    if (destination.path === path) return { id: destination.id, parent: null }
  }
  if (path === '/sign') return { id: 'home', parent: '/' }
  if (path === '/send' || path.startsWith('/agreements/') || path.startsWith('/requests/')) {
    return { id: 'agreements', parent: '/agreements' }
  }
  if (SETTINGS_ROUTES.some((route) => path === route || path.startsWith(route))) {
    return { id: 'settings', parent: '/settings' }
  }
  return { id: null, parent: '/' }
}
